import { ValidationErrors } from 'final-form';
import React, { ReactNode } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import { TextField } from '~common/components/form/field-text.component';
import { Box } from '~common/components/box.component';
import { FormButton } from '~common/components/button/form-button.component';
import { FormFields } from '~common/components/form/form-fields.component';
import { onSuccessReaction } from '~common/helper/mobx-tasks';
import { useTask } from '~common/hook/use-task';
import { validate } from '~common/validator';
import { required } from '~common/validator/validations';
import { TaskOverlay } from '~web/view/components/layout/task-overlay.component';
import { createSeatConfigurationTaskFactory } from '~admin/interactor/location/perform-create-seat-configuration';
import { SeatConfigurationData } from '~admin/model/seat-configuration-data';
import { Button } from '~common/components/button/button.component';
import { Color } from '~common/model/basics';
import { Size } from '~common/components/button/button.helper';
import {
  SeatConfigurationCategoryItemFields,
  SeatConfigurationCategoryItemFormData,
} from '~admin/view/components/details/event-details/event-location-details/seat-configuration-category-item-fields.component';
import { PlusIcon } from '~web/view/components/ui/icon/plus.icon';
import { getSeatConfigurationCategoryInitialValues } from '~admin/view/components/create-event/helper/create-seat-configuration-initial-values.helper';
import { Column } from '~common/components/column.component';
import { getSeatConfigurationRequest } from '~admin/view/components/create-event/helper/seat-configuration-request.helper';

interface Props {
  eventLocationId: number;
  onSeatConfigurationCreated?: (seatConfiguration: SeatConfigurationData) => void;
  onCancel?: VoidFunction;
}

export interface AddSeatConfigurationFormData {
  seatConfigurationName: string;
  categoryData: SeatConfigurationCategoryItemFormData[];
}

export const AddSeatConfigurationOverlay: React.FunctionComponent<Props> = ({
  eventLocationId,
  onSeatConfigurationCreated,
  onCancel,
}) => {
  const { t } = useTranslation();
  const createSeatConfigurationTask = useTask(createSeatConfigurationTaskFactory);

  onSuccessReaction(createSeatConfigurationTask, (result: SeatConfigurationData) => {
    onSeatConfigurationCreated?.(result);
  });

  const onSubmit = (formData: AddSeatConfigurationFormData): void => {
    createSeatConfigurationTask(getSeatConfigurationRequest(formData, eventLocationId));
  };

  const renderForm = (renderProps: FormRenderProps<AddSeatConfigurationFormData>): ReactNode => (
    <FormFields onSubmit={renderProps.handleSubmit} autoComplete={'off'}>
      <RestrictBox>
        <TextField name={'seatConfigurationName'} label={t('createEvent.seatConfiguration.name')} />
      </RestrictBox>
      <RestrictBox>
        <FieldArray name={'categoryData'}>
          {({ fields }): ReactNode => {
            const onRemove = (index: number): void => {
              fields.remove(index);
            };

            const onAdd = (): void => {
              fields.push(getSeatConfigurationCategoryInitialValues());
            };

            return (
              <CategoryFields>
                {fields.map((name: string, index: number) => (
                  <SeatConfigurationCategoryItemFields
                    key={name}
                    name={name}
                    onRemove={(): void => onRemove(index)}
                  />
                ))}
                <Box>
                  <Button color={Color.White} size={Size.Small} onClick={onAdd}>
                    <StyledPlusIcon color={Color.Black} width={14} height={14} />
                    {t('createEvent.seatConfiguration.category.add')}
                  </Button>
                </Box>
              </CategoryFields>
            );
          }}
        </FieldArray>
      </RestrictBox>
      <ButtonContainer>
        <CreateButton task={createSeatConfigurationTask} isInvalid={renderProps.invalid}>
          {t('createEvent.createButton')}
        </CreateButton>
      </ButtonContainer>
    </FormFields>
  );

  return (
    <StyledTaskOverlay
      title={t('createEvent.seatConfiguration.createNew')}
      onCancel={onCancel}
      task={createSeatConfigurationTask}
    >
      <Form
        onSubmit={onSubmit}
        render={renderForm}
        mutators={{
          ...arrayMutators,
        }}
        validate={(values: AddSeatConfigurationFormData): ValidationErrors | undefined => ({
          seatConfigurationName: validate(values.seatConfigurationName, [required()]),
        })}
      />
    </StyledTaskOverlay>
  );
};

const StyledTaskOverlay = styled(TaskOverlay)`
  min-width: 95% !important;
`;

const CategoryFields = styled(Column)`
  flex: 1;
`;

const CreateButton = styled(FormButton)`
  && {
    margin-top: 32px;
  }
`;

const ButtonContainer = styled(Box)`
  width: 100%;
  justify-content: flex-end;
`;

const RestrictBox = styled(Box)`
  flex: 1 1 auto;
  margin-top: 24px;
`;

const StyledPlusIcon = styled(PlusIcon)`
  fill: currentColor;
  margin-right: 12px;
`;



// WEBPACK FOOTER //
// ./view/components/details/event-details/event-location-details/add-seat-configuration-overlay.component.tsx